<template>
  <div>
    <v-dialog :value="value" max-width="1400" width="95vw" persistent @input="handleDialogInput">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Editar lista de precios: {{ productDisplayName }}</span>
          <v-btn icon @click="closeDialog" :disabled="saving">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-subtitle>
          Producto: {{ productId || "N/D" }}
        </v-card-subtitle>

        <v-card-text>
          <div class="add-price-list-toolbar">
            <v-btn
              text
              color="primary"
              :disabled="loading || addingToPriceList"
              @click="showAddPriceList = !showAddPriceList"
            >
              <v-icon left>mdi-plus</v-icon>
              Agregar a otra lista
            </v-btn>
          </div>

          <v-expand-transition>
            <v-card v-if="showAddPriceList" outlined class="add-price-list-panel mb-4">
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="newPriceList.PriceListName"
                      :items="availablePriceLists"
                      item-text="id"
                      item-value="id"
                      label="Lista de precios"
                      dense
                      outlined
                      hide-details
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="newPriceList.ItemOriginalPrice"
                      type="number"
                      min="0"
                      step="0.0001"
                      label="Precio inicial"
                      dense
                      outlined
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2" class="text-right">
                    <v-btn
                      color="primary"
                      :loading="addingToPriceList"
                      :disabled="!newPriceList.PriceListName || loading"
                      @click="addProductToPriceList"
                    >
                      Agregar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-expand-transition>

          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-linear>

          <v-alert
            v-if="!loading && !priceRows.length"
            type="info"
            outlined
            dense
          >
            No se encontraron listas de precios para este producto.
          </v-alert>

          <v-simple-table v-if="!loading && priceRows.length" class="price-list-editor-table">
            <colgroup>
              <col class="price-list-column" />
              <col class="range-column" />
              <col class="current-price-column" />
              <col class="new-price-column" />
              <col class="discount-amount-column" />
              <col class="discount-percent-column" />
              <col class="discounted-price-column" />
              <col class="actions-column" />
            </colgroup>
            <thead>
              <tr>
                <th>Lista de precios</th>
                <th class="text-left">Rangos</th>
                <th class="text-right">Precio actual</th>
                <th>Nuevo Precio</th>
                <th>Descuento $</th>
                <th>Descuento %</th>
                <th class="text-right">Precio con descuento</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in priceRows" :key="getRowKey(row)">
                <td class="price-input-cell">
                  <div class="price-list-select-row">
                    <v-select
                    v-model="row.PriceListName"
                    :items="getPriceListOptions(row)"
                    item-text="id"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    :disabled="!row.editPriceList"
                    class="editable-list-field"
                    @change="syncRowAction(row)"
                  ></v-select>
                  <v-btn
                    icon
                    small
                    :color="row.editPriceList ? 'primary' : 'grey'"
                    :title="row.editPriceList ? 'Bloquear lista' : 'Editar lista'"
                    @click="row.editPriceList = !row.editPriceList"
                  >
                    <v-icon small>{{ row.editPriceList ? "mdi-check" : "mdi-pencil" }}</v-icon>
                  </v-btn>
                  </div>
                </td>
                <td class="text-left">
                  <div class="range-inputs">
                    <v-text-field
                      v-model.number="row.InitialRange"
                      type="number"
                      min="1"
                      dense
                      outlined
                      hide-details
                      placeholder="De"
                      class="range-input"
                      :error="isRangeError(row)"
                      @blur="handleRangeChange(row)"
                    ></v-text-field>
                    <span class="range-input-separator">a</span>
                    <v-text-field
                      v-model.number="row.FinalRange"
                      type="number"
                      min="1"
                      dense
                      outlined
                      hide-details
                      placeholder="A"
                      class="range-input"
                      :error="isRangeError(row)"
                      @blur="handleRangeChange(row)"
                    ></v-text-field>
                  </div>
                </td>
                <td class="text-right price-current-value">{{ formatMoney(row.ItemOriginalPrice) }}</td>
                <td class="price-input-cell">
                  <v-text-field
                    v-model.number="row.ItemBasePrice"
                    type="number"
                    min="0"
                    step="0.0001"
                    dense
                    outlined
                    hide-details
                    class="editable-price-field"
                    @input="handleBasePriceChange(row)"
                    @blur="normalizeRowBasePrice(row)"
                  ></v-text-field>
                </td>
                <td class="price-input-cell">
                  <div class="discount-field-row">
                    <v-text-field
                      v-model.number="row.ItemPriceDiscountAmount"
                      type="number"
                      min="0"
                      step="0.0001"
                      dense
                      outlined
                      hide-details
                      prefix="$"
                      class="editable-price-field"
                      @input="handleDiscountAmountChange(row)"
                      @blur="normalizeRowDiscountAmount(row)"
                    ></v-text-field>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          small
                          color="grey"
                          class="discount-info-icon"
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>
                        Descuento guardado: ${{ formatDiscount(getOriginalDiscount(row, "ItemPriceDiscountAmount")) }}
                        ({{ formatDiscount(getOriginalDiscount(row, "ItemPriceDiscountPercent")) }}%)
                      </span>
                    </v-tooltip>
                  </div>
                </td>
                <td class="price-input-cell">
                  <v-text-field
                    v-model.number="row.ItemPriceDiscountPercent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.0001"
                    dense
                    outlined
                    hide-details
                    suffix="%"
                    class="editable-price-field"
                    @input="handleDiscountPercentChange(row)"
                    @blur="normalizeRowDiscountPercent(row)"
                  ></v-text-field>
                </td>
                <td class="text-right price-current-value">
                  {{ formatMoney(row.ItemNewPrice) }}
                </td>
                <td class="text-center">
                  <div class="price-actions">
                    <v-btn
                      icon
                      small
                      color="primary"
                      :disabled="saving || addingToPriceList"
                      title="Duplicar fila"
                      @click="duplicateRow(row)"
                    >
                      <v-icon small>mdi-content-duplicate</v-icon>
                    </v-btn>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="delete-tooltip-activator" v-bind="attrs" v-on="on">
                          <v-btn
                            v-if="row.PriceListName === 'Precios por defecto'"
                            icon
                            small
                            disabled
                            class="delete-price-button"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                          <v-btn
                            v-else
                            icon
                            small
                            color="red"
                            class="delete-price-button"
                            :disabled="saving || addingToPriceList || deletingPrice"
                            @click="requestDeleteRow(row)"
                          >
                            <v-icon small color="red">mdi-delete</v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>
                        {{ row.PriceListName === "Precios por defecto"
                          ? "Los precios por defecto no se pueden eliminar"
                          : "Eliminar precio" }}
                      </span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog" :disabled="saving">Cerrar</v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="loading || !hasChanges"
            @click="saveChanges"
          >
            Guardar precios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :value="alert.show"
      attach="body"
      max-width="520"
      persistent
      content-class="product-price-alert-dialog"
      @input="alert.show = $event"
    >
      <v-card :class="['product-price-alert-card', `product-price-alert-${alert.type}`]">
        <v-card-title class="product-price-alert-header">
          <v-icon dark left>mdi-alert-circle</v-icon>
          {{ alert.header }}
        </v-card-title>
        <v-card-text class="product-price-alert-body">{{ alert.body }}</v-card-text>
        <v-card-actions class="product-price-alert-actions">
          <v-spacer></v-spacer>
          <v-btn class="product-price-alert-button" text @click="closeAlert">Aceptar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :value="deleteConfirmation.show"
      attach="body"
      max-width="520"
      persistent
      content-class="product-price-alert-dialog"
      @input="deleteConfirmation.show = $event"
    >
      <v-card class="product-price-alert-card product-price-alert-warning">
        <v-card-title class="product-price-alert-header">
          <v-icon dark left>mdi-alert-circle</v-icon>
          Confirmar eliminación
        </v-card-title>
        <v-card-text class="product-price-alert-body">
          ¿Está seguro de eliminar este precio de la lista
          “{{ deleteConfirmation.row ? deleteConfirmation.row.PriceListName : "" }}”?
          <div v-if="deleteConfirmation.row" class="delete-price-details">
            <div><strong>Producto:</strong> {{ deleteConfirmation.row.ItemName || productId }}</div>
            <div><strong>Precio:</strong> {{ formatMoney(deleteConfirmation.row.ItemBasePrice) }}</div>
            <div>
              <strong>Descuento:</strong>
              ${{ formatDiscount(deleteConfirmation.row.ItemPriceDiscountAmount) }}
              ({{ formatDiscount(deleteConfirmation.row.ItemPriceDiscountPercent) }}%)
            </div>
            <div><strong>Precio final:</strong> {{ formatMoney(deleteConfirmation.row.ItemNewPrice) }}</div>
            <div>
              <strong>Rango:</strong>
              {{ deleteConfirmation.row.InitialRange && deleteConfirmation.row.FinalRange
                ? `${deleteConfirmation.row.InitialRange} a ${deleteConfirmation.row.FinalRange}`
                : "Sin rango" }}
            </div>
          </div>
          <v-checkbox
            v-model="deleteConfirmation.confirmed"
            dark
            color="white"
            class="delete-confirmation-checkbox"
            label="Confirmo que deseo eliminar este precio"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions class="product-price-alert-actions">
          <v-spacer></v-spacer>
          <v-btn class="delete-confirmation-cancel" text @click="cancelDeleteRow">Cancelar</v-btn>
          <v-btn
            class="product-price-alert-button"
            text
            :loading="deletingPrice"
            :disabled="!deleteConfirmation.confirmed"
            @click="deletePriceRow"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "ProductPriceListEditorDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    productId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      addingToPriceList: false,
      deletingPrice: false,
      showAddPriceList: false,
      priceRows: [],
      allPriceLists: [],
      newPriceList: {
        PriceListName: null,
        ItemOriginalPrice: 0,
      },
      rangeErrors: {},
      deleteConfirmation: {
        show: false,
        confirmed: false,
        row: null,
      },
      originalRowsSnapshot: [],
      alert: {
        type: "success",
        show: false,
        header: "",
        body: "",
      },
    };
  },
  computed: {
    hasChanges() {
      return this.priceRows.some((row) => ["updated", "created"].includes(row.action));
    },
    currentUser() {
      return JSON.parse(localStorage.getItem("user")) || {};
    },
    productDisplayName() {
      return (this.priceRows[0] && this.priceRows[0].ItemName) || this.productId || "N/D";
    },
    availablePriceLists() {
      const currentNames = new Set(this.priceRows.map((row) => row.PriceListName));
      return this.allPriceLists.filter((item) => !currentNames.has(item.id));
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen && this.productId) {
          this.loadPriceLists();
        }

        if (!isOpen) {
          this.resetState();
        }
      },
    },
    productId(newValue, oldValue) {
      if (this.value && newValue && newValue !== oldValue) {
        this.loadPriceLists();
      }
    },
  },
  methods: {
    handleDialogInput(isOpen) {
      this.$emit("input", isOpen);
    },
    closeDialog() {
      this.$emit("input", false);
    },
    resetState() {
      this.loading = false;
      this.saving = false;
      this.addingToPriceList = false;
      this.deletingPrice = false;
      this.showAddPriceList = false;
      this.priceRows = [];
      this.allPriceLists = [];
      this.newPriceList = { PriceListName: null, ItemOriginalPrice: 0 };
      this.rangeErrors = {};
      this.deleteConfirmation = { show: false, confirmed: false, row: null };
      this.originalRowsSnapshot = [];
    },
    showAlert(type, header, body) {
      this.alert.type = type || "warning";
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
    },
    closeAlert() {
      this.alert.show = false;
    },
    getRowKey(row) {
      const rowId = row.id ?? row.Pk_billing_Price_List ?? row.uniqueID;

      return `${row.ID_Item || this.productId}-${rowId || "row"}`;
    },
    getNextRange(row) {
      const initialRange = this.toNumber(row.InitialRange, 0);
      const finalRange = this.toNumber(row.FinalRange, 0);

      if (initialRange <= 0 || finalRange <= 0 || finalRange < initialRange) {
        return { InitialRange: null, FinalRange: null };
      }

      const priceListName = String(row.PriceListName || "").trim().toLowerCase();
      const ranges = this.priceRows
        .filter((item) => {
          return String(item.PriceListName || "").trim().toLowerCase() === priceListName &&
            this.toNumber(item.InitialRange, 0) > 0 &&
            this.toNumber(item.FinalRange, 0) > 0;
        })
        .map((item) => this.toNumber(item.FinalRange, 0));
      const lastRange = Math.max(finalRange, ...ranges);
      const rangeSize = finalRange - initialRange + 1;

      return {
        InitialRange: lastRange + 1,
        FinalRange: lastRange + rangeSize,
      };
    },
    duplicateRow(row) {
      const nextRange = this.getNextRange(row);
      const duplicatedRow = {
        ...JSON.parse(JSON.stringify(row)),
        id: null,
        Pk_billing_Price_List: null,
        uniqueID: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        InitialRange: nextRange.InitialRange,
        FinalRange: nextRange.FinalRange,
        action: "created",
        editPriceList: true,
        ID_User_Insert: this.currentUser.id,
        isDeleted: 0,
      };

      this.priceRows.push(duplicatedRow);
    },
    requestDeleteRow(row) {
      this.deleteConfirmation.row = row;
      this.deleteConfirmation.confirmed = false;
      this.deleteConfirmation.show = true;
    },
    cancelDeleteRow() {
      this.deleteConfirmation = { show: false, confirmed: false, row: null };
    },
    async deletePriceRow() {
      const row = this.deleteConfirmation.row;
      if (!row || !this.deleteConfirmation.confirmed) {
        return;
      }

      if (!row.id) {
        this.priceRows = this.priceRows.filter((item) => item !== row);
        this.cancelDeleteRow();
        return;
      }

      this.deletingPrice = true;

      try {
        await this.$API.priceList.deleteProductPriceList(this.productId, row.id);
        this.cancelDeleteRow();
        await this.loadPriceLists();
        this.showAlert("success", "Éxito", "Precio eliminado correctamente.");
      } catch (error) {
        this.showAlert("warning", "Advertencia", error || "No se pudo eliminar el precio.");
      } finally {
        this.deletingPrice = false;
      }
    },
    toNumber(value, fallback = 0) {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    },
    formatMoney(value) {
      return this.toNumber(value).toFixed(4);
    },
    formatDiscount(value) {
      return this.toNumber(value).toFixed(2);
    },
    getOriginalDiscount(row, field) {
      const original = this.originalRowsSnapshot.find(
        (item) => this.getRowKey(item) === this.getRowKey(row)
      );

      return original ? original[field] : row[field];
    },
    getPriceListOptions(row) {
      const options = this.availablePriceLists.slice();

      if (row.PriceListName && !options.some((item) => item.id === row.PriceListName)) {
        options.push({ id: row.PriceListName });
      }

      return options;
    },
    normalizeActive(value) {
      if (value && typeof value === "object" && Array.isArray(value.data)) {
        return value.data[0] ? 1 : 0;
      }

      return value ? 1 : 0;
    },
    roundValue(value, decimals = 4) {
      return parseFloat(this.toNumber(value).toFixed(decimals));
    },
    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    recalculateFromDiscountAmount(row) {
      const basePrice = this.toNumber(row.ItemBasePrice);
      const discountAmount = this.clamp(this.toNumber(row.ItemPriceDiscountAmount), 0, basePrice);
      const discountPercent = basePrice > 0
        ? (discountAmount / basePrice) * 100
        : 0;

      row.ItemBasePrice = this.roundValue(basePrice);
      row.ItemPriceDiscountAmount = this.roundValue(discountAmount);
      row.ItemPriceDiscountPercent = this.roundValue(discountPercent);
      row.ItemNewPrice = this.roundValue(basePrice - discountAmount);
    },
    recalculateFromDiscountPercent(row) {
      const basePrice = this.toNumber(row.ItemBasePrice);
      const discountPercent = this.clamp(this.toNumber(row.ItemPriceDiscountPercent), 0, 100);
      const discountAmount = basePrice > 0
        ? (basePrice * discountPercent) / 100
        : 0;

      row.ItemBasePrice = this.roundValue(basePrice);
      row.ItemPriceDiscountPercent = this.roundValue(discountPercent);
      row.ItemPriceDiscountAmount = this.roundValue(discountAmount);
      row.ItemNewPrice = this.roundValue(basePrice - discountAmount);
    },
    normalizeRows(rows = []) {
      return rows.map((item) => {
        const originalPrice = this.toNumber(item.ItemOriginalPrice);
        const discountAmount = this.toNumber(item.ItemPriceDiscountAmount);
        const discountPercent = this.toNumber(item.ItemPriceDiscountPercent);
        const newPrice = this.toNumber(
          item.ItemNewPrice,
          Math.max(originalPrice - discountAmount, 0)
        );
        const basePrice = originalPrice;

        const normalizedRow = {
          ...item,
          ID_Item: item.ID_Item || this.productId,
          PriceListName: item.PriceListName || item.pricelistname || item.id || "",
          ItemOriginalPrice: originalPrice,
          ItemBasePrice: basePrice,
          ItemNewPrice: newPrice,
          ItemPriceDiscountAmount: discountAmount,
          ItemPriceDiscountPercent: discountPercent,
          ID_Business: item.ID_Business || this.currentUser.businessid || 0,
          ID_Branch: item.ID_Branch || this.currentUser.branch || 0,
          Active: item.Active == null ? 1 : item.Active,
          action: item.action || null,
          editPriceList: false,
        };

        if (discountPercent > 0) {
          this.recalculateFromDiscountPercent(normalizedRow);
        } else {
          this.recalculateFromDiscountAmount(normalizedRow);
        }

        return normalizedRow;
      });
    },
    async loadPriceLists() {
      this.loading = true;

      try {
        const [response, allPriceLists] = await Promise.all([
          this.$API.priceList.getProductPriceLists(this.productId),
          this.$API.priceList.priceListNoData(),
        ]);

        this.priceRows = this.normalizeRows(response || []);
        this.allPriceLists = (allPriceLists || []).filter(
          (item, index, list) => list.findIndex((candidate) => candidate.id === item.id) === index
        );
        this.newPriceList.ItemOriginalPrice = this.getDefaultPrice();
        this.originalRowsSnapshot = JSON.parse(JSON.stringify(this.priceRows));
      } catch (error) {
        console.error(error);
        this.showAlert("warning", "Advertencia", error || "No se pudieron cargar los precios del producto.");
      } finally {
        this.loading = false;
      }
    },
    getDefaultPrice() {
      const firstRow = this.priceRows[0];
      return firstRow ? this.toNumber(firstRow.ItemBasePrice || firstRow.ItemOriginalPrice) : 0;
    },
    async addProductToPriceList() {
      if (!this.newPriceList.PriceListName) {
        return;
      }

      this.addingToPriceList = true;

      try {
        const response = await this.$API.priceList.addProductToPriceList(this.productId, {
          PriceListName: this.newPriceList.PriceListName,
          ItemOriginalPrice: this.toNumber(this.newPriceList.ItemOriginalPrice),
        });

        this.showAddPriceList = false;
        this.newPriceList.PriceListName = null;
        await this.loadPriceLists();
        const warning = response && response.externalStoreSyncWarning;
        this.showAlert(
          warning ? "warning" : "success",
          warning ? "Producto agregado" : "Éxito",
          warning ? "Producto agregado a la lista de precios correctamente. " + warning : "Producto agregado a la lista de precios correctamente."
        );
      } catch (error) {
        this.showAlert("warning", "Advertencia", error || "No se pudo agregar el producto a la lista de precios.");
      } finally {
        this.addingToPriceList = false;
      }
    },
    handleBasePriceChange(row) {
      row.ItemBasePrice = this.clamp(this.toNumber(row.ItemBasePrice), 0, Number.MAX_SAFE_INTEGER);
      if (this.toNumber(row.ItemPriceDiscountPercent) > 0) {
        this.recalculateFromDiscountPercent(row);
      } else {
        this.recalculateFromDiscountAmount(row);
      }
      this.syncRowAction(row);
    },
    handleDiscountAmountChange(row) {
      this.recalculateFromDiscountAmount(row);
      this.syncRowAction(row);
    },
    handleDiscountPercentChange(row) {
      this.recalculateFromDiscountPercent(row);
      this.syncRowAction(row);
    },
    normalizeRowBasePrice(row) {
      row.ItemBasePrice = this.roundValue(row.ItemBasePrice);
      this.handleBasePriceChange(row);
    },
    normalizeRowDiscountAmount(row) {
      row.ItemPriceDiscountAmount = this.roundValue(row.ItemPriceDiscountAmount);
      this.handleDiscountAmountChange(row);
    },
    normalizeRowDiscountPercent(row) {
      row.ItemPriceDiscountPercent = this.roundValue(row.ItemPriceDiscountPercent);
      this.handleDiscountPercentChange(row);
    },
    handleRangeChange(row) {
      const initialRange = this.toNumber(row.InitialRange, 0);
      const finalRange = this.toNumber(row.FinalRange, 0);

      row.InitialRange = initialRange > 0 ? Math.floor(initialRange) : null;
      row.FinalRange = finalRange > 0 ? Math.floor(finalRange) : null;
      this.$delete(this.rangeErrors, this.getRowKey(row));
      this.syncRowAction(row);
    },
    isRangeError(row) {
      return Boolean(this.rangeErrors[this.getRowKey(row)]);
    },
    markRangeError(row) {
      this.$set(this.rangeErrors, this.getRowKey(row), true);
    },
    syncRowAction(row) {
      if (row.action === "created") {
        return;
      }

      const original = this.originalRowsSnapshot.find(
        (item) => this.getRowKey(item) === this.getRowKey(row)
      );

      if (!original) {
        row.action = "updated";
        return;
      }

      const changed =
        this.toNumber(original.ItemBasePrice) !== this.toNumber(row.ItemBasePrice) ||
        this.toNumber(original.ItemPriceDiscountAmount) !== this.toNumber(row.ItemPriceDiscountAmount) ||
        this.toNumber(original.ItemPriceDiscountPercent) !== this.toNumber(row.ItemPriceDiscountPercent) ||
        this.toNumber(original.ItemNewPrice) !== this.toNumber(row.ItemNewPrice) ||
        original.PriceListName !== row.PriceListName ||
        this.toNumber(original.InitialRange) !== this.toNumber(row.InitialRange) ||
        this.toNumber(original.FinalRange) !== this.toNumber(row.FinalRange);

      row.action = changed ? "updated" : null;
    },
    buildPayload() {
      return this.priceRows
        .filter((row) => row.action === "updated")
        .map((row) => ({
          id: row.id,
          ID_Item: row.ID_Item,
          PriceListName: row.PriceListName,
          ID_User_Update: this.currentUser.id,
          ItemOriginalPrice: this.toNumber(row.ItemBasePrice),
          ItemPriceDiscountAmount: this.toNumber(row.ItemPriceDiscountAmount),
          ItemPriceDiscountPercent: this.toNumber(row.ItemPriceDiscountPercent),
          ItemNewPrice: this.toNumber(row.ItemNewPrice),
          InitialRange: row.InitialRange || null,
          FinalRange: row.FinalRange || null,
        }));
    },
    buildCreatedPayload() {
      return this.priceRows
        .filter((row) => row.action === "created")
        .map((row) => ({
          PriceListName: row.PriceListName,
          ID_Item: this.productId,
          ID_Business: row.ID_Business || this.currentUser.businessid,
          ID_Branch: row.ID_Branch || this.currentUser.branch,
          IsGlobalBranch: row.IsGlobalBranch == null ? false : Boolean(this.normalizeActive(row.IsGlobalBranch)),
          ItemCurrency: row.ItemCurrency || "$",
          Active: row.Active == null ? 1 : this.normalizeActive(row.Active),
          ID_User_Insert: this.currentUser.id,
          action: "created",
          ItemOriginalPrice: this.toNumber(row.ItemBasePrice),
          ItemPriceDiscountAmount: this.toNumber(row.ItemPriceDiscountAmount),
          ItemPriceDiscountPercent: this.toNumber(row.ItemPriceDiscountPercent),
          ItemNewPrice: this.toNumber(row.ItemNewPrice),
          InitialRange: row.InitialRange || null,
          FinalRange: row.FinalRange || null,
          isDeleted: 0,
        }));
    },
    validatePriceRows() {
      const rowsByPriceListAndRange = {};
      const rangesByPriceList = {};
      this.rangeErrors = {};

      for (const row of this.priceRows) {
        const hasInitialRange = this.toNumber(row.InitialRange, 0) > 0;
        const hasFinalRange = this.toNumber(row.FinalRange, 0) > 0;

        if (hasInitialRange !== hasFinalRange) {
          this.markRangeError(row);
          this.showAlert(
            "warning",
            "Rangos incompletos",
            `La lista "${row.PriceListName || "Sin nombre"}" debe tener los rangos De y A, o dejar ambos vacíos.`
          );
          return false;
        }

        if (hasInitialRange && Number(row.InitialRange) >= Number(row.FinalRange)) {
          this.markRangeError(row);
          this.showAlert(
            "warning",
            "Rango inválido",
            `El rango de la lista "${row.PriceListName || "Sin nombre"}" debe comenzar con un valor menor que el final.`
          );
          return false;
        }

        if (hasInitialRange && hasFinalRange) {
          const priceListName = String(row.PriceListName || "").trim().toLowerCase();
          const currentInitialRange = Number(row.InitialRange);
          const currentFinalRange = Number(row.FinalRange);
          const existingRanges = rangesByPriceList[priceListName] || [];
          const overlappingRange = existingRanges.find(
            (range) => currentInitialRange <= range.final && currentFinalRange >= range.initial
          );

          if (overlappingRange) {
            this.markRangeError(row);
            this.markRangeError(overlappingRange.row);
            this.showAlert(
              "warning",
              "Rangos superpuestos",
              `El rango ${row.InitialRange} a ${row.FinalRange} se superpone con el rango ${overlappingRange.initial} a ${overlappingRange.final} de la lista "${row.PriceListName || "Sin nombre"}".`
            );
            return false;
          }

          rangesByPriceList[priceListName] = [
            ...existingRanges,
            { initial: currentInitialRange, final: currentFinalRange, row },
          ];
        }

        const priceListName = String(row.PriceListName || "").trim().toLowerCase();
        const rangeKey = hasInitialRange && hasFinalRange
          ? `${priceListName}|${row.InitialRange}|${row.FinalRange}`
          : `${priceListName}|without-range`;

        if (rowsByPriceListAndRange[rangeKey]) {
          this.markRangeError(row);
          this.markRangeError(rowsByPriceListAndRange[rangeKey]);
          const rangeDescription = hasInitialRange
            ? `con el rango ${row.InitialRange} a ${row.FinalRange}`
            : "sin rangos";

          this.showAlert(
            "warning",
            "Precios duplicados",
            `El producto ya tiene otro precio en la lista "${row.PriceListName || "Sin nombre"}" ${rangeDescription}. Use un rango diferente.`
          );
          return false;
        }

        rowsByPriceListAndRange[rangeKey] = row;
      }

      return true;
    },
    async saveChanges() {
      const records = this.buildPayload();
      const createdRecords = this.buildCreatedPayload();

      if (!records.length && !createdRecords.length) {
        this.showAlert("info", "Información", "No hay cambios para guardar.");
        return;
      }

      if (!this.validatePriceRows()) {
        return;
      }

      this.saving = true;

      try {
        const operations = [];

        if (records.length) {
          operations.push(this.$API.priceList.updateProductPriceLists(this.productId, { records }));
        }
        if (createdRecords.length) {
          operations.push(this.$API.priceList.addPriceList({ records: createdRecords, deletedRecords: [] }));
        }
        const responses = await Promise.all(operations);
        // Las dos operaciones devuelven el mismo aviso de la tienda: se muestra una vez.
        const warning = responses.map((response) => response && response.externalStoreSyncWarning).find(Boolean);

        this.$emit("saved", {
          productId: this.productId,
          records: [...records, ...createdRecords],
        });
        await this.loadPriceLists();
        this.showAlert(
          warning ? "warning" : "success",
          warning ? "Precios guardados" : "Éxito",
          warning ? "Precios guardados correctamente. " + warning : "Precios guardados correctamente."
        );
      } catch (error) {
        console.error(error);
        this.showAlert("warning", "Advertencia", error || "No se pudieron guardar los precios.");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.price-list-editor-table th,
.price-list-editor-table td {
  vertical-align: middle;
}

.add-price-list-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.add-price-list-panel {
  background: #fafbfc;
}

.product-price-alert-card {
  background: #41464c !important;
  color: whitesmoke;
}

.product-price-alert-header,
.product-price-alert-body,
.product-price-alert-actions {
  background: #41464c;
  color: whitesmoke !important;
}

.product-price-alert-body {
  font-size: 16px;
  font-weight: bold;
}

.delete-price-details {
  margin-top: 14px;
  padding: 10px 12px;
  border-left: 3px solid #f29d35;
  background: rgba(255, 255, 255, 0.08);
  font-size: 14px;
  font-weight: normal;
  line-height: 1.7;
}

.product-price-alert-button {
  min-width: 140px;
  margin: 12px;
  color: whitesmoke !important;
  background: #3498db !important;
}

.product-price-alert-warning .product-price-alert-button {
  background: #f29d35 !important;
}

::v-deep .delete-confirmation-checkbox .v-label {
  color: #ffffff !important;
}

.delete-confirmation-cancel {
  color: #ffffff !important;
}

::v-deep .delete-price-button .v-icon {
  color: #e3191e !important;
}

::v-deep .delete-price-button.v-btn--disabled .v-icon {
  color: #9e9e9e !important;
}

::v-deep .delete-confirmation-checkbox {
  margin-top: 24px !important;
}

.price-list-editor-table table {
  table-layout: fixed;
  width: 100%;
}

.price-list-editor-table th {
  padding: 12px 8px;
  color: #5f6673;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  text-transform: none;
  white-space: nowrap;
}

.price-list-editor-table td {
  padding: 12px 8px;
}

.price-list-column {
  width: 22%;
}

.range-column {
  width: 13%;
}

.current-price-column {
  width: 7%;
}

.new-price-column {
  width: 13%;
}

.discount-amount-column {
  width: 12%;
}

.discount-percent-column {
  width: 10%;
}

.discounted-price-column {
  width: 14%;
}

.actions-column {
  width: 7%;
}

.price-current-value {
  color: #344054;
  font-variant-numeric: tabular-nums;
}

.price-input-cell {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.range-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 6px 10px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fbff 0%, #e3eeff 100%);
  border: 1px solid rgba(25, 118, 210, 0.14);
}

.range-badge__tag {
  font-weight: 600;
  letter-spacing: 0.2px;
}

.range-badge__values {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-variant-numeric: tabular-nums;
}

.range-badge__value {
  font-weight: 700;
  color: #1f3a5f;
}

.range-badge__separator {
  color: #6b7280;
  font-size: 12px;
}

.range-empty {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px solid #d7dbe3;
  color: #81858e;
  font-size: 13px;
}

.editable-price-field {
  max-width: 135px;
  margin-left: auto;
}

.editable-list-field {
  max-width: 250px;
}

.price-list-select-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.price-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
}

.delete-tooltip-activator {
  display: inline-flex;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 4px;
}

.range-input {
  width: 90px;
  max-width: 90px;
}

.range-input-separator {
  color: #667085;
  font-size: 12px;
}

.discount-field-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.discount-info-icon {
  cursor: help;
}

</style>
